import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { formatApiErrorDetail } from "@/lib/api";
import BrowserWarning, { browserWarningShown, needsBrowserWarning } from "@/components/BrowserWarning";

const TXT = {
  fr: { loading: "ON PRÉPARE TON PROJET…", limit: "Limite atteinte pour cette connexion sans compte.", login: "Se connecter", retry: "Réessayer", home: "Accueil" },
  en: { loading: "SETTING UP YOUR PROJECT…", limit: "Limit reached for this connection without an account.", login: "Sign in", retry: "Retry", home: "Home" },
};

/* /start : point d'entrée « Commencer maintenant » — session anonyme si besoin, avertissement
   navigateur une fois par session, puis nouveau projet dans le studio. */
export default function Start() {
  const { user, startAnonymous } = useAuth();
  const { lang } = useI18n();
  const t = TXT[lang] || TXT.fr;
  const navigate = useNavigate();
  const [warn, setWarn] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(false);

  const goStudio = () => navigate("/studio?new=1", { replace: true });

  useEffect(() => {
    if (user === undefined || started.current) return;
    started.current = true;
    (async () => {
      try {
        if (user === null) await startAnonymous();
        if (needsBrowserWarning() && !browserWarningShown()) { setWarn(true); return; }
        goStudio();
      } catch (e) {
        started.current = false;
        setError(formatApiErrorDetail(e.response?.data?.detail) || t.limit);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (warn) return <BrowserWarning onContinue={goStudio} />;
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      {error ? (
        <div className="w-full max-w-md bg-card border border-border p-8 text-center" data-testid="start-error">
          <p className="text-sm text-foreground mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login" data-testid="start-login-link" className="rounded-full bg-primary text-white font-osd text-[11px] tracking-[0.08em] uppercase h-11 px-6 inline-flex items-center justify-center">{t.login}</Link>
            <button onClick={() => { setError(""); started.current = false; window.location.reload(); }} data-testid="start-retry" className="h-11 px-6 border border-border font-osd text-[11px] tracking-[0.08em] uppercase">{t.retry}</button>
          </div>
        </div>
      ) : (
        <div className="font-osd text-sm text-[#fc1c46] animate-pulse" data-testid="start-loading">{t.loading}</div>
      )}
    </div>
  );
}
